// VOY-461 — Voy My Trip API client (SSE consumer).
//
// build-trip / refine-trip stream Server-Sent Events. EventSource can't POST
// or set an auth header, so we stream the POST response body via fetch +
// ReadableStream and parse the `event:`/`data:` frames ourselves.

import { supabase } from '@/lib/supabase'
import type {
  BuildTripRequest,
  RefineTripRequest,
  ComponentResult,
  VoyMyTripDone,
} from '@/types/voy-my-trip'
import type { TripComponent, ComponentReaction } from '@/lib/search/refinement-constraints'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

export interface VoyMyTripStreamHandlers {
  onStart?: (data: {
    framing_pending?: boolean
    reactions?: Record<TripComponent, ComponentReaction>
    ops?: unknown[]
    message?: string
  }) => void
  onComponent?: (data: ComponentResult) => void
  onDone?: (data: VoyMyTripDone) => void
  onError?: (err: { error: string }) => void
}

async function authHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const token = (await supabase.auth.getSession()).data.session?.access_token
  if (token) headers['Authorization'] = `Bearer ${token}`
  return headers
}

/**
 * POST `path` and consume the SSE stream, dispatching parsed events to the
 * handlers. Resolves when the stream closes. Pass an AbortSignal to cancel.
 */
// Abort the stream if no frame arrives for this long. The build streams
// components incrementally (framing → flights → hotels), so a healthy stream
// never idles this long; a hung backend would otherwise lock the UI in
// `streaming: true` forever.
const STREAM_IDLE_MS = 45_000

async function streamPost(
  path: string,
  body: unknown,
  handlers: VoyMyTripStreamHandlers,
  signal?: AbortSignal,
): Promise<void> {
  // Local controller so an idle timeout can abort the fetch; chain the caller's
  // signal (user cancel) into it.
  const ac = new AbortController()
  if (signal) {
    if (signal.aborted) ac.abort()
    else signal.addEventListener('abort', () => ac.abort(), { once: true })
  }
  let timedOut = false
  let idleTimer: ReturnType<typeof setTimeout> | undefined
  const armIdle = () => {
    if (idleTimer) clearTimeout(idleTimer)
    idleTimer = setTimeout(() => { timedOut = true; ac.abort() }, STREAM_IDLE_MS)
  }

  let res: Response
  try {
    armIdle()
    res = await fetch(`${API_URL}${path}`, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify(body),
      signal: ac.signal,
    })
  } catch (err) {
    if (idleTimer) clearTimeout(idleTimer)
    if (timedOut) handlers.onError?.({ error: 'Voy took too long to respond. Please try again.' })
    else if (!signal?.aborted) handlers.onError?.({ error: err instanceof Error ? err.message : 'Network error' })
    return
  }

  if (!res.ok || !res.body) {
    if (idleTimer) clearTimeout(idleTimer)
    let msg = `Request failed (${res.status})`
    try {
      const j = await res.json()
      msg = j?.error || msg
    } catch {
      /* non-JSON error body */
    }
    handlers.onError?.({ error: msg })
    return
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  const dispatch = (rawEvent: string) => {
    // One SSE frame: lines of "event: x" / "data: y".
    let eventName = 'message'
    const dataLines: string[] = []
    for (const line of rawEvent.split('\n')) {
      if (line.startsWith('event:')) eventName = line.slice(6).trim()
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim())
    }
    if (dataLines.length === 0) return
    let data: unknown
    try {
      data = JSON.parse(dataLines.join('\n'))
    } catch {
      return
    }
    switch (eventName) {
      case 'start':
        handlers.onStart?.(data as Parameters<NonNullable<VoyMyTripStreamHandlers['onStart']>>[0])
        break
      case 'component':
        handlers.onComponent?.(data as ComponentResult)
        break
      case 'done':
        handlers.onDone?.(data as VoyMyTripDone)
        break
      case 'error':
        handlers.onError?.(data as { error: string })
        break
    }
  }

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      armIdle() // healthy frame → reset the idle watchdog
      buffer += decoder.decode(value, { stream: true })
      // SSE frames are separated by a blank line.
      let sep
      while ((sep = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, sep)
        buffer = buffer.slice(sep + 2)
        if (frame.trim()) dispatch(frame)
      }
    }
    if (buffer.trim()) dispatch(buffer)
  } catch (err) {
    if (timedOut) handlers.onError?.({ error: 'Voy took too long to respond. Please try again.' })
    else if (!signal?.aborted) handlers.onError?.({ error: err instanceof Error ? err.message : 'Stream interrupted' })
    // user-initiated abort (signal) → silent
  } finally {
    if (idleTimer) clearTimeout(idleTimer)
  }
}

export interface TripPreview {
  preview: string
  framing: {
    destination: string | null
    origin: string | null
    depart_date: string | null
    return_date: string | null
    pax_adults: number | null
    vibe: string | null
  }
}

/**
 * Framing-only preview — a live, memory-grounded inferred-intent sentence for
 * the search card before the user commits. No flight/hotel search runs, so it
 * returns fast. Pass an AbortSignal to cancel a stale request when the user
 * toggles away. Returns null on failure (the card keeps its default copy).
 */
export async function previewTrip(
  req: { intent?: string; components?: string[] } = {},
  signal?: AbortSignal,
): Promise<TripPreview | null> {
  try {
    const res = await fetch(`${API_URL}/api/agent/preview-trip`, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify(req),
      signal,
    })
    if (!res.ok) return null
    return (await res.json()) as TripPreview
  } catch {
    return null
  }
}

/** Assemble — zero/minimal input → a personalized trip, streamed. */
export function buildTrip(
  req: BuildTripRequest,
  handlers: VoyMyTripStreamHandlers,
  signal?: AbortSignal,
): Promise<void> {
  return streamPost('/api/agent/build-trip', req, handlers, signal)
}

/** Refine — NL utterance or pill-computed nextSet, streamed. */
export function refineTrip(
  req: RefineTripRequest,
  handlers: VoyMyTripStreamHandlers,
  signal?: AbortSignal,
): Promise<void> {
  return streamPost('/api/agent/refine-trip', req, handlers, signal)
}

/** Re-research a saved search through the same agent. */
export function reResearchSavedSearch(
  savedSearchId: string,
  handlers: VoyMyTripStreamHandlers,
  signal?: AbortSignal,
): Promise<void> {
  return streamPost('/api/agent/re-research-saved-search', { savedSearchId }, handlers, signal)
}
